import { Check, X } from 'lucide-react'

import { cn } from '~/lib/utils.ts'
import { Skeleton } from '~/elements/Skeleton.tsx'
import { useBillingCatalogQuery } from '~/features/billing/hooks/useBillingHooks'
import { usePlatformSettings } from '~/features/auth/hooks/useAuthQueries'
import { PLAN_NAMES, PLAN_ORDER } from '~/features/billing/constants'

function FeatureCell({ value }: { value: boolean | string | undefined }) {
  if (value === undefined) return <span className="text-content3">—</span>

  if (typeof value === 'boolean') {
    return value ?
        <Check className="mx-auto h-4 w-4 text-accent" />
      : <X className="mx-auto h-4 w-4 text-content3" />
  }

  return <span className="text-content">{value}</span>
}

function ComparisonHeading() {
  return (
    <div className="mb-6 text-center">
      <p className="mb-2 font-mono text-xs tracking-widest text-content3 uppercase">Compare plans</p>
      <p className="font-mono text-xl font-bold text-content">Feature comparison</p>
    </div>
  )
}

/** Feature-by-feature plan comparison table, served by the billing catalog. */
export function PricingComparison({ className }: { className?: string }) {
  const { data: settings, isPending: settingsPending } = usePlatformSettings()
  const { data: catalog, isLoading } = useBillingCatalogQuery()

  if (settingsPending || (settings?.selfHosted === false && isLoading)) {
    return (
      <div className={cn('w-full', className)}>
        <ComparisonHeading />
        <Skeleton enabled className="h-96 w-full rounded-2xl" />
      </div>
    )
  }

  if (!catalog?.comparison?.length) return null

  const columns = PLAN_ORDER.map((id) => ({
    id,
    label: catalog.plans.find((plan) => plan.id === id)?.name ?? PLAN_NAMES[id]
  }))

  return (
    <div className={cn('w-full', className)}>
      <ComparisonHeading />

      <div className="overflow-x-auto rounded-2xl border">
        <table className="w-full border-collapse font-mono text-sm">
          <thead>
            <tr className="bg-secondary">
              <th className="border-b px-5 py-4 text-left font-semibold text-content">Features</th>
              {columns.map((column) => (
                <th className="border-b px-5 py-4 text-center font-semibold text-content" key={column.id}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.comparison.map((row, index) => (
              <tr className={index % 2 ? 'bg-secondary' : undefined} key={row.name}>
                <td className="border-b px-5 py-4 text-left text-content">{row.name}</td>
                {columns.map((column) => (
                  <td className="border-b px-5 py-4 text-center text-content2" key={column.id}>
                    <FeatureCell value={row[column.id]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
